import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { productAPI, cartAPI, orderAPI, userAPI, handleApiError, showApiError } from '../../services/api';
import { requestPayment } from '@portone/browser-sdk/v2';
import { useSubscription } from '../../context/SubscriptionContext';
import './ProductPurchasePage.css';

const resolveThumbnailSrc = (thumbnailPath) => {
  if (!thumbnailPath) return null;
  if (thumbnailPath.startsWith('http')) return thumbnailPath;
  const apiBase = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8080/api' : '/api');
  const origin = apiBase.replace(/\/api\/?$/, '');
  return thumbnailPath.startsWith('/') ? `${origin}${thumbnailPath}` : `${origin}/${thumbnailPath}`;
};

const PRODUCT_CATEGORY_VALUES = ['SUBSCRIPTION', 'MACHINE', 'PACKAGE'];

const CATEGORY_LABEL_KO = {
  SUBSCRIPTION: '구독형',
  MACHINE: '장비',
  PACKAGE: '패키지',
};

function categoryLabelKo(category) {
  if (category == null || category === '') return '알 수 없음';
  return CATEGORY_LABEL_KO[category] || String(category);
}

function categoryRequiresBillingPeriod(category) {
  return category === 'SUBSCRIPTION' || category === 'PACKAGE';
}

const emptyForm = () => ({
  name: '',
  price: '',
  description: '',
  category: 'SUBSCRIPTION',
  billingPeriod: '',
});

const DIGIT_KO = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];

/** 1~9999만 한글로 (만 단위 안쪽 덩어리) */
function readUnder10000(n) {
  if (n <= 0 || n >= 10000) return '';
  const qian = Math.floor(n / 1000);
  const bai = Math.floor((n % 1000) / 100);
  const shi = Math.floor((n % 100) / 10);
  const yi = n % 10;
  let s = '';
  if (qian) s += (qian === 1 ? '' : DIGIT_KO[qian]) + '천';
  if (bai) s += (bai === 1 ? '' : DIGIT_KO[bai]) + '백';
  if (shi) s += (shi === 1 ? '' : DIGIT_KO[shi]) + '십';
  if (yi) s += DIGIT_KO[yi];
  return s;
}

/** 0 이상 정수 원 → 한글 금액 (…원) */
function wonToKorean(floorN) {
  if (!Number.isFinite(floorN) || floorN < 0) return '';
  if (floorN === 0) return '영원';
  const units = ['', '만', '억', '조', '경'];
  let num = floorN;
  let u = 0;
  const parts = [];
  while (num > 0 && u < units.length) {
    const chunk = num % 10000;
    if (chunk !== 0) {
      const read = readUnder10000(chunk) || DIGIT_KO[chunk] || String(chunk);
      parts.unshift(read + units[u]);
    }
    num = Math.floor(num / 10000);
    u += 1;
  }
  return parts.join('') + '원';
}

/** state에는 숫자만(콤마 없음). 붙여넣기 시 콤마 제거 */
function sanitizePriceDigits(value) {
  const d = String(value).replace(/,/g, '').replace(/\D/g, '');
  if (d === '') return '';
  return d.replace(/^0+/, '') || '0';
}

/** 입력칸 표시용 천 단위 콤마 */
function formatPriceWithCommas(digitsOnly) {
  if (!digitsOnly) return '';
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function koreanWonCaption(digitsOnly) {
  if (!digitsOnly) return null;
  const n = Number(digitsOnly);
  if (!Number.isFinite(n) || n < 0) return null;
  return wonToKorean(Math.trunc(n));
}

function calculateVatInclusiveAmount(digitsOnly) {
  if (!digitsOnly) return null;
  const n = Number(digitsOnly);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 1.1);
}

function formatWon(value) {
  if (value == null || Number.isNaN(Number(value))) return '0원';
  return `${Number(value).toLocaleString('ko-KR')}원`;
}

function formatPhoneForPortOne(phoneNumber) {
  const digits = String(phoneNumber ?? '').replace(/\D/g, '');
  if (!digits) return undefined;
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
}

function buildPortOneCustomer(user) {
  const fullName = user?.name?.trim() || user?.username?.trim();
  if (!fullName) return undefined;

  const customer = { fullName };
  const phoneNumber = formatPhoneForPortOne(user?.phoneNumber);
  if (phoneNumber) {
    customer.phoneNumber = phoneNumber;
  }
  const email = user?.email?.trim();
  if (email) {
    customer.email = email;
  }
  if (user?.id != null) {
    customer.customerId = String(user.id);
  }
  return customer;
}

const PORTONE_STORE_ID = process.env.REACT_APP_PORTONE_STORE_ID;
const PORTONE_CHANNEL_KEY = process.env.REACT_APP_PORTONE_CHANNEL_KEY;
const PORTONE_REDIRECT_URL = process.env.REACT_APP_PORTONE_REDIRECT_URL;

const ProductPurchasePage = ({ user }) => {
  const location = useLocation();
  const isAdmin = user?.role === 'ADMIN';
  const {
    refreshSubscription,
    connectedPlants,
    getSubscriptionForPlant,
  } = useSubscription();
  const [selectedPowerLocationId, setSelectedPowerLocationId] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [existingThumbnailPath, setExistingThumbnailPath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [buyingProductId, setBuyingProductId] = useState(null);
  const [cartingProductId, setCartingProductId] = useState(null);
  const [cart, setCart] = useState({ cartId: null, items: [], totalAmount: 0 });
  const [cartLoading, setCartLoading] = useState(true);
  const [removingCartItemId, setRemovingCartItemId] = useState(null);
  const [cartCheckingOut, setCartCheckingOut] = useState(false);
  const [showCartPanel, setShowCartPanel] = useState(false);
  const [redirectConfirming, setRedirectConfirming] = useState(false);
  const [checkoutUser, setCheckoutUser] = useState(null);
  const [registerPriceWithVat, setRegisterPriceWithVat] = useState(true);

  const ensurePortOneConfig = () => {
    if (!PORTONE_STORE_ID || !PORTONE_CHANNEL_KEY) {
      window.alert(
        '포트원 설정이 없습니다.\n.env에 REACT_APP_PORTONE_STORE_ID, REACT_APP_PORTONE_CHANNEL_KEY를 설정해 주세요.'
      );
      return false;
    }
    return true;
  };

  const ensurePowerLocationSelected = () => {
    if (isAdmin) return true;
    if (!selectedPowerLocationId) {
      window.alert('구독을 연장할 발전소를 선택해 주세요.');
      return false;
    }
    return true;
  };

  const getSelectedPlantLabel = () => {
    const plant = connectedPlants.find(
      (item) => String(item.id) === String(selectedPowerLocationId)
    );
    return plant?.name || `발전소 #${selectedPowerLocationId}`;
  };

  const openPortOnePayment = async ({ paymentId, orderName, amount, currency }) => {
    const redirectUrl =
      PORTONE_REDIRECT_URL ||
      `${window.location.origin}/products`;
    const safeCurrency =
      currency === 'KRW' || currency === 'CURRENCY_KRW' || !currency
        ? 'KRW'
        : currency;

    let paymentUser = checkoutUser || user;
    if (user?.id && !paymentUser?.phoneNumber) {
      try {
        const profile = await userAPI.get(user.id);
        paymentUser = { ...user, ...profile };
        setCheckoutUser(paymentUser);
      } catch {
        paymentUser = checkoutUser || user;
      }
    }

    const customer = buildPortOneCustomer(paymentUser);

    const response = await requestPayment({
      storeId: PORTONE_STORE_ID,
      channelKey: PORTONE_CHANNEL_KEY,
      paymentId,
      orderName,
      totalAmount: Number(amount ?? 0),
      currency: safeCurrency,
      payMethod: 'CARD',
      locale: 'KO_KR',
      ...(customer ? { customer } : {}),
      windowType: {
        pc: 'IFRAME',
        mobile: 'REDIRECTION',
      },
      redirectUrl,
    });

    if (response && response.code) {
      throw new Error(response.message || '결제가 취소되었거나 실패했습니다.');
    }
  };

  const fetchProducts = useCallback(async ({ silent } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await productAPI.getList();
      setProducts(data.products || []);
    } catch (e) {
      const info = handleApiError(e);
      if (!silent) {
        setError(info.message);
        setProducts([]);
      } else {
        showApiError(e, '목록을 다시 불러오지 못했습니다.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (isAdmin || connectedPlants.length === 0) return;

    const requestedId = location.state?.powerLocationId;
    if (requestedId != null) {
      const exists = connectedPlants.some((plant) => String(plant.id) === String(requestedId));
      if (exists) {
        setSelectedPowerLocationId(String(requestedId));
        return;
      }
    }

    setSelectedPowerLocationId((current) => {
      if (current && connectedPlants.some((plant) => String(plant.id) === String(current))) {
        return current;
      }
      return String(connectedPlants[0].id);
    });
  }, [connectedPlants, isAdmin, location.state]);

  useEffect(() => {
    if (!user?.id) {
      setCheckoutUser(null);
      return undefined;
    }

    let cancelled = false;

    const loadCheckoutUser = async () => {
      try {
        const profile = await userAPI.get(user.id);
        if (!cancelled) {
          setCheckoutUser({ ...user, ...profile });
        }
      } catch {
        if (!cancelled) {
          setCheckoutUser(user);
        }
      }
    };

    loadCheckoutUser();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const loadCart = useCallback(async ({ silent } = {}) => {
    if (!silent) setCartLoading(true);
    try {
      const data = await cartAPI.getMyCart();
      setCart({
        cartId: data?.cartId ?? null,
        items: data?.items ?? [],
        totalAmount: data?.totalAmount ?? 0,
      });
    } catch (err) {
      if (!silent) showApiError(err);
      setCart({ cartId: null, items: [], totalAmount: 0 });
    } finally {
      if (!silent) setCartLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const redirectedPaymentId = search.get('paymentId');
    const code = search.get('code');
    const message = search.get('message');
    if (!redirectedPaymentId) return;

    const clearQuery = () => {
      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, '', cleanUrl);
    };

    if (code) {
      window.alert(`결제가 취소되었거나 실패했습니다.\n${message || code}`);
      clearQuery();
      return;
    }

    const run = async () => {
      setRedirectConfirming(true);
      try {
        await orderAPI.confirmPayment(redirectedPaymentId);
        window.alert('결제가 승인되었습니다.');
        refreshSubscription();
        await loadCart({ silent: true });
      } catch (err) {
        showApiError(err);
      } finally {
        clearQuery();
        setRedirectConfirming(false);
      }
    };
    run();
  }, [loadCart, refreshSubscription]);

  useEffect(() => {
    if (!thumbnailFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setThumbnailFile(null);
    setExistingThumbnailPath(null);
    setModalMode('create');
    setRegisterPriceWithVat(true);
  };

  const openCreate = () => {
    setModalMode('create');
    setEditingId(null);
    setForm(emptyForm());
    setThumbnailFile(null);
    setExistingThumbnailPath(null);
    setRegisterPriceWithVat(true);
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setModalMode('edit');
    setEditingId(product.id);
    const cat = product.category;
    const category =
      cat && PRODUCT_CATEGORY_VALUES.includes(cat) ? cat : 'SUBSCRIPTION';
    setForm({
      name: product.name ?? '',
      price:
        product.price != null && Number.isFinite(Number(product.price))
          ? sanitizePriceDigits(String(Math.floor(Number(product.price))))
          : '',
      description: product.description ?? '',
      category,
      billingPeriod: product.billingPeriod != null ? String(product.billingPeriod) : '',
    });
    setThumbnailFile(null);
    setExistingThumbnailPath(product.thumbnailPath ?? null);
    setRegisterPriceWithVat(false);
    setModalOpen(true);
  };

  const buildPayload = () => {
    const name = form.name.trim();
    let price =
      form.price === '' || form.price === null || form.price === undefined
        ? null
        : Number(form.price);
    if (price != null && registerPriceWithVat) {
      price = calculateVatInclusiveAmount(form.price) ?? price;
    }
    const description = form.description.trim() || null;
    const category = PRODUCT_CATEGORY_VALUES.includes(form.category) ? form.category : 'SUBSCRIPTION';
    let billingPeriod = null;
    if (categoryRequiresBillingPeriod(category)) {
      if (form.billingPeriod === '' || form.billingPeriod === null || form.billingPeriod === undefined) {
        billingPeriod = null;
      } else {
        const n = parseInt(String(form.billingPeriod), 10);
        billingPeriod = Number.isNaN(n) ? null : n;
      }
    }
    return { name, price, description, category, billingPeriod };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      alert('제품명을 입력해 주세요.');
      return;
    }
    if (categoryRequiresBillingPeriod(form.category)) {
      const bp = form.billingPeriod === '' ? null : parseInt(String(form.billingPeriod), 10);
      if (bp === null || Number.isNaN(bp) || bp < 1) {
        alert('구독형/패키지는 연장 개월 수(1 이상의 정수)를 입력해 주세요.');
        return;
      }
    }

    const base = buildPayload();
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await productAPI.create(base, thumbnailFile);
      } else {
        await productAPI.update(editingId, base, thumbnailFile);
      }
      closeModal();
      await fetchProducts({ silent: true });
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`「${product.name}」 제품을 삭제할까요?`)) return;
    try {
      await productAPI.delete(product.id);
      await fetchProducts({ silent: true });
    } catch (err) {
      showApiError(err);
    }
  };

  const handlePurchaseClick = (product) => {
    const run = async () => {
      if (!ensurePortOneConfig() || !ensurePowerLocationSelected()) return;
      setBuyingProductId(product.id);
      try {
        const prepared = await orderAPI.prepareDirectCheckout(
          product.id,
          1,
          Number(selectedPowerLocationId)
        );
        await openPortOnePayment({
          paymentId: prepared.paymentId,
          orderName: product.name,
          amount: prepared.amount,
          currency: prepared.currency,
        });
        await orderAPI.confirmPayment(prepared.paymentId);
        window.alert('결제가 승인되었습니다.');
        refreshSubscription();
      } catch (err) {
        if (err?.response) {
          showApiError(err);
        } else {
          window.alert(err?.message || '결제 처리 중 오류가 발생했습니다.');
        }
      } finally {
        setBuyingProductId(null);
      }
    };
    run();
  };

  const handleAddToCartClick = (product) => {
    const run = async () => {
      setCartingProductId(product.id);
      try {
        await cartAPI.addItem(product.id, 1);
        window.alert(`「${product.name}」 상품을 장바구니에 담았습니다.`);
        await loadCart({ silent: true });
        setShowCartPanel(true);
      } catch (err) {
        showApiError(err);
      } finally {
        setCartingProductId(null);
      }
    };
    run();
  };

  const handleRemoveCartItem = async (item) => {
    if (!window.confirm(`「${item.productName}」을(를) 장바구니에서 삭제할까요?`)) return;
    setRemovingCartItemId(item.cartItemId);
    try {
      await cartAPI.removeItem(item.cartItemId);
      await loadCart({ silent: true });
    } catch (err) {
      showApiError(err);
    } finally {
      setRemovingCartItemId(null);
    }
  };

  const handleCartCheckout = async () => {
    const itemIds = (cart?.items || []).map((item) => item.cartItemId);
    if (itemIds.length === 0) return;
    if (!ensurePortOneConfig() || !ensurePowerLocationSelected()) return;
    setCartCheckingOut(true);
    try {
      const prepared = await orderAPI.prepareCartCheckout(
        itemIds,
        Number(selectedPowerLocationId)
      );
      await openPortOnePayment({
        paymentId: prepared.paymentId,
        orderName: '장바구니 결제',
        amount: prepared.amount,
        currency: prepared.currency,
      });
      await orderAPI.confirmPayment(prepared.paymentId);
      window.alert('장바구니 결제가 승인되었습니다.');
      refreshSubscription();
      await loadCart({ silent: true });
    } catch (err) {
      if (err?.response) {
        showApiError(err);
      } else {
        window.alert(err?.message || '결제 처리 중 오류가 발생했습니다.');
      }
    } finally {
      setCartCheckingOut(false);
    }
  };

  const formattedProducts = useMemo(
    () =>
      products.map((p) => ({
        ...p,
        thumbnailSrc: resolveThumbnailSrc(p.thumbnailPath),
      })),
    [products]
  );

  const modalThumbSrc = previewUrl || (existingThumbnailPath ? resolveThumbnailSrc(existingThumbnailPath) : null);
  const priceKoreanCaption = modalOpen ? koreanWonCaption(form.price) : null;
  const vatInclusiveAmount = modalOpen ? calculateVatInclusiveAmount(form.price) : null;
  const vatInclusiveCaption = vatInclusiveAmount != null ? wonToKorean(vatInclusiveAmount) : null;
  const cartItems = cart?.items || [];

  return (
    <div className="product-purchase-page">
      <div className="product-purchase-header">
        <div className="product-purchase-header-top">
          <div className="product-purchase-header-texts">
            <h1 className="product-purchase-title">제품 구매</h1>
            {!isAdmin && connectedPlants.length > 0 && (
              <div className="product-power-location-selector">
                <label htmlFor="product-power-location-select">구독 대상 발전소</label>
                <select
                  id="product-power-location-select"
                  value={selectedPowerLocationId}
                  onChange={(event) => setSelectedPowerLocationId(event.target.value)}
                >
                  {connectedPlants.map((plant) => {
                    const subscription = getSubscriptionForPlant(plant.id);
                    const statusLabel = subscription?.active
                      ? '이용 중'
                      : subscription
                        ? '만료'
                        : '구독 없음';
                    return (
                      <option key={plant.id} value={plant.id}>
                        {plant.name} ({statusLabel})
                      </option>
                    );
                  })}
                </select>
                {selectedPowerLocationId && (
                  <p className="product-power-location-hint">
                    선택한 발전소: <strong>{getSelectedPlantLabel()}</strong>에 구독이 적용됩니다.
                  </p>
                )}
              </div>
            )}
            {!isAdmin && connectedPlants.length === 0 && (
              <p className="product-power-location-empty">
                연결된 발전소가 없습니다. 관리자에게 발전소 연결을 요청한 뒤 구독을 진행해 주세요.
              </p>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="product-admin-bar">
            <button type="button" className="product-btn product-btn-primary" onClick={openCreate}>
              제품 등록
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        className="product-cart-fab"
        onClick={() => setShowCartPanel(true)}
        aria-label="장바구니 열기"
        title="장바구니"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 6h18l-1 11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2L3 6Z" />
          <path d="M9 6a3 3 0 0 1 6 0" />
        </svg>
        {cartItems.length > 0 && <span className="product-cart-count">{cartItems.length}</span>}
      </button>

      {loading && <div className="product-purchase-state">불러오는 중…</div>}
      {!loading && error && <div className="product-purchase-state product-purchase-error">{error}</div>}
      {redirectConfirming && (
        <div className="product-purchase-state">결제 승인 처리 중…</div>
      )}
      {!loading && !error && (
        <>
          {formattedProducts.length === 0 ? (
            <div className="product-purchase-state">등록된 제품이 없습니다.</div>
          ) : (
            <ul className="product-list">
              {formattedProducts.map((product) => (
                <li key={product.id} className="product-card">
                  <div className="product-card-top">
                    <div className="product-card-thumb">
                      {product.thumbnailSrc ? (
                        <img src={product.thumbnailSrc} alt="" loading="lazy" />
                      ) : (
                        <div className="product-card-thumb-placeholder">이미지 없음</div>
                      )}
                    </div>
                    <div className="product-card-body">
                      <div className="product-card-text">
                        <h2 className="product-card-name">{product.name}</h2>
                        {product.description && (
                          <p className="product-card-desc">{product.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="product-card-bottom">
                    <div className="product-card-tags">
                      <span
                        className={
                          product.category === 'SUBSCRIPTION'
                            ? 'product-tag product-tag-sub'
                            : product.category === 'MACHINE'
                              ? 'product-tag product-tag-machine'
                              : product.category === 'PACKAGE'
                                ? 'product-tag product-tag-package'
                                : 'product-tag'
                        }
                      >
                        {categoryLabelKo(product.category)}
                      </span>
                      {categoryRequiresBillingPeriod(product.category) && product.billingPeriod != null && (
                        <span className="product-tag product-tag-muted">
                          구독 기간 +{product.billingPeriod}개월
                        </span>
                      )}
                    </div>
                    <div className="product-card-price-bar">
                      <div className="product-card-price-line">
                        <span className="product-card-price-label">가격:</span>{' '}
                        {product.price != null ? (
                          <span className="product-card-price-value">
                            {Number(product.price).toLocaleString('ko-KR')}원
                          </span>
                        ) : (
                          <span className="product-card-price-value product-card-price-value--muted">가격 미정</span>
                        )}
                      </div>
                      <div className="product-card-price-actions">
                        <button
                          type="button"
                          className="product-btn product-btn-primary product-btn-buy"
                          onClick={() => handlePurchaseClick(product)}
                          disabled={buyingProductId === product.id || (!isAdmin && connectedPlants.length === 0)}
                        >
                          {buyingProductId === product.id ? '준비 중…' : '구매'}
                        </button>
                        <button
                          type="button"
                          className="product-btn product-btn-cart"
                          onClick={() => handleAddToCartClick(product)}
                          disabled={cartingProductId === product.id}
                        >
                          {cartingProductId === product.id ? '담는 중…' : '장바구니 담기'}
                        </button>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="product-card-admin-actions">
                        <button type="button" className="product-btn product-btn-sm" onClick={() => openEdit(product)}>
                          수정
                        </button>
                        <button
                          type="button"
                          className="product-btn product-btn-sm product-btn-danger"
                          onClick={() => handleDelete(product)}
                        >
                          삭제
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

        </>
      )}

      {showCartPanel && (
        <div className="product-inline-cart-overlay" role="presentation" onClick={() => setShowCartPanel(false)}>
          <aside className="product-inline-cart-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="product-inline-cart-header">
              <h2>장바구니</h2>
              <div className="product-inline-cart-header-right">
                <span>제품 구매 페이지에서 바로 확인</span>
                <button
                  type="button"
                  className="product-inline-cart-close"
                  aria-label="장바구니 닫기"
                  onClick={() => setShowCartPanel(false)}
                >
                  ×
                </button>
              </div>
            </div>

            {cartLoading ? (
              <div className="product-inline-cart-state">장바구니를 불러오는 중…</div>
            ) : cartItems.length === 0 ? (
              <div className="product-inline-cart-state">장바구니가 비어 있습니다.</div>
            ) : (
              <>
                <ul className="product-inline-cart-list">
                  {cartItems.map((item) => (
                    <li key={item.cartItemId} className="product-inline-cart-item">
                      <div className="product-inline-cart-item-main">
                        <strong>{item.productName}</strong>
                        <span>
                          수량 {item.quantity}개 / 단가 {formatWon(item.unitPrice)}
                        </span>
                      </div>
                      <div className="product-inline-cart-item-right">
                        <em>{formatWon(item.lineTotal)}</em>
                        <button
                          type="button"
                          className="product-btn product-btn-sm product-btn-danger"
                          disabled={removingCartItemId === item.cartItemId}
                          onClick={() => handleRemoveCartItem(item)}
                        >
                          {removingCartItemId === item.cartItemId ? '삭제 중…' : '삭제'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="product-inline-cart-summary">
                  <div>
                    총 결제 금액 <strong>{formatWon(cart.totalAmount)}</strong>
                  </div>
                  <button
                    type="button"
                    className="product-btn product-btn-primary"
                    disabled={cartCheckingOut || (!isAdmin && !selectedPowerLocationId)}
                    onClick={handleCartCheckout}
                  >
                    {cartCheckingOut ? '결제 준비 중…' : '장바구니 결제'}
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      )}

      {modalOpen && (
        <div className="product-modal-overlay" role="presentation" onClick={closeModal}>
          <div
            className="product-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-modal-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="product-modal-header">
              <h2 id="product-modal-title">{modalMode === 'create' ? '제품 등록' : '제품 수정'}</h2>
              <button type="button" className="product-modal-close" onClick={closeModal} aria-label="닫기">
                ×
              </button>
            </div>
            <form className="product-modal-body" onSubmit={handleSubmit}>
              <label className="product-field">
                <span className="product-field-label">제품명 *</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  maxLength={200}
                  required
                />
              </label>
              <label className="product-field">
                <span className="product-field-label">가격 (원)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={formatPriceWithCommas(form.price)}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, price: sanitizePriceDigits(e.target.value) }))
                  }
                  placeholder="비워 두면 미정"
                />
                {priceKoreanCaption && (
                  <span className="product-price-korean" aria-live="polite">
                    {priceKoreanCaption}
                  </span>
                )}
                {vatInclusiveAmount != null && (
                  <span className="product-price-vat" aria-live="polite">
                    부가세 포함 금액: {formatWon(vatInclusiveAmount)}
                    {vatInclusiveCaption ? ` (${vatInclusiveCaption})` : ''}
                  </span>
                )}
              </label>
              <label className="product-field product-field-inline product-field-vat-option">
                <input
                  type="checkbox"
                  checked={registerPriceWithVat}
                  onChange={(event) => setRegisterPriceWithVat(event.target.checked)}
                />
                <span>부가세 포함 가격으로 등록</span>
              </label>
              <label className="product-field">
                <span className="product-field-label">설명</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={4}
                />
              </label>
              <label className="product-field">
                <span className="product-field-label">유형 *</span>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      category: e.target.value,
                      billingPeriod: categoryRequiresBillingPeriod(e.target.value) ? f.billingPeriod : '',
                    }))
                  }
                >
                  <option value="SUBSCRIPTION">구독형</option>
                  <option value="MACHINE">장비</option>
                  <option value="PACKAGE">패키지</option>
                </select>
              </label>
              {categoryRequiresBillingPeriod(form.category) && (
                <label className="product-field">
                  <span className="product-field-label">연장 개월 수 *</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={form.billingPeriod}
                    onChange={(e) => setForm((f) => ({ ...f, billingPeriod: e.target.value }))}
                    placeholder="예: 1, 3, 12"
                  />
                </label>
              )}
              <label className="product-field">
                <span className="product-field-label">썸네일 이미지</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                />
              </label>
              {modalThumbSrc && (
                <div className="product-modal-preview">
                  <span className="product-field-label">미리보기</span>
                  <div className="product-modal-preview-frame">
                    <img src={modalThumbSrc} alt="" />
                  </div>
                </div>
              )}
              <div className="product-modal-footer">
                <button type="button" className="product-btn" onClick={closeModal} disabled={saving}>
                  취소
                </button>
                <button type="submit" className="product-btn product-btn-primary" disabled={saving}>
                  {saving ? '저장 중…' : modalMode === 'create' ? '등록' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPurchasePage;
